import React, { useState, useEffect } from 'react';
import { X, Send, Palette, Zap, MapPin, AlertCircle, HelpCircle, CheckCircle2, Bug } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { createErrorReport } from '../../services/api/errorReports';

const ERROR_TYPES = [
  { id: 'bug_visual', label: 'Bug Visual', icon: Palette, color: 'text-purple-400' },
  { id: 'performance', label: 'Lentidão', icon: Zap, color: 'text-amber-400' },
  { id: 'mapa', label: 'Mapa / GPS', icon: MapPin, color: 'text-emerald-400' },
  { id: 'dados_incorretos', label: 'Dado Errado', icon: AlertCircle, color: 'text-rose-400' },
  { id: 'outro', label: 'Outro', icon: HelpCircle, color: 'text-cyan-400' }
];

export const ErrorReportModal = ({ isOpen, onClose, initialContext = '' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [formData, setFormData] = useState({
    error_type: 'bug_visual',
    title: '',
    description: '',
    user_name: '',
    contact: ''
  });

  useEffect(() => {
    if (isOpen && initialContext) {
      setFormData(prev => ({
        ...prev,
        title: initialContext,
        error_type: initialContext.toLowerCase().includes('ponto') ? 'dados_incorretos' : prev.error_type
      }));
    }
  }, [isOpen, initialContext]);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      // Captura automática de dados de telemetria técnica
      const screenDetails = typeof window !== 'undefined'
        ? `${window.innerWidth}x${window.innerHeight} (tela: ${window.screen?.width || 'N/D'}x${window.screen?.height || 'N/D'})`
        : '';
      const userAgent = typeof navigator !== 'undefined'
        ? `${navigator.userAgent} [Viewport: ${screenDetails}]`
        : '';
      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

      await createErrorReport({
        ...formData,
        user_agent: userAgent,
        page_url: pageUrl
      });

      setSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 2500);
    } catch (err) {
      setErrorMessage(err.message || 'Falha ao enviar relatório de erro. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setErrorMessage(null);
    setFormData({
      error_type: 'bug_visual',
      title: '',
      description: '',
      user_name: '',
      contact: ''
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[115] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-300">
      <div className={`relative border rounded-2xl max-w-lg w-full p-6 md:p-7 shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto ${
        isDark ? 'bg-slate-800 border-rose-500/30 shadow-rose-500/10' : 'bg-white border-slate-200 shadow-slate-200/60'
      }`}>
        {/* Botão de Fechar */}
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
            isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
          }`}
          title="Fechar"
        >
          <X size={20} />
        </button>

        {submitted ? (
          <div className="text-center py-8 flex flex-col items-center">
            <div className="w-16 h-16 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mb-4 animate-bounce">
              <CheckCircle2 size={36} />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              Relatório Registrado!
            </h3>
            <p className={`text-sm max-w-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Obrigado por nos avisar! Nossa equipe de desenvolvimento investigará essa falha para manter a experiência do mapa sempre impecável.
            </p>
          </div>
        ) : (
          <>
            {/* Cabeçalho */}
            <div className="flex items-center gap-3 mb-5 pr-8">
              <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-500 border border-rose-500/20 shrink-0">
                <Bug size={22} />
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Reportar Erro ou Bug
                </h2>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Encontrou algo que não funcionou como esperado? Relate para nós corrigirmos.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo de Erro */}
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Tipo de Ocorrência
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ERROR_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.error_type === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, error_type: type.id })}
                        className={`flex items-center gap-2 p-2.5 rounded-xl border text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-500/25'
                            : isDark
                              ? 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
                              : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <Icon size={16} className={isSelected ? 'text-white' : type.color} />
                        <span>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Título Resumido */}
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Título Breve <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: O botão de rota não responde no celular"
                  className={`w-full px-3.5 py-2.5 text-sm rounded-xl border transition-all outline-none focus:ring-2 focus:ring-rose-500/40 ${
                    isDark 
                      ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600' 
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>

              {/* Descrição Detalhada */}
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Descrição Detalhada <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva o que ocorreu, os passos para acontecer e o comportamento esperado..."
                  className={`w-full px-3.5 py-2.5 text-sm rounded-xl border transition-all outline-none focus:ring-2 focus:ring-rose-500/40 resize-none ${
                    isDark 
                      ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600' 
                      : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>

              {/* Informações Opcionais do Usuário */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Seu Nome (Opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.user_name}
                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                    placeholder="Ex: Maria Clara"
                    className={`w-full px-3.5 py-2.5 text-sm rounded-xl border transition-all outline-none focus:ring-2 focus:ring-rose-500/40 ${
                      isDark 
                        ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600' 
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Contato (Opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="E-mail ou WhatsApp"
                    className={`w-full px-3.5 py-2.5 text-sm rounded-xl border transition-all outline-none focus:ring-2 focus:ring-rose-500/40 ${
                      isDark 
                        ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-600' 
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                  />
                </div>
              </div>

              {/* Telemetria automática */}
              <div className={`p-2.5 rounded-xl border text-[11px] flex items-center gap-2 ${
                isDark ? 'bg-slate-900/60 border-slate-700/60 text-slate-400' : 'bg-slate-50 border-slate-200 text-slate-500'
              }`}>
                <span className="shrink-0 font-bold">ℹ️</span>
                <span>Dados de resolução, navegador e URL atual serão anexados automaticamente para ajudar nossa equipe técnica.</span>
              </div>

              {/* Mensagem de Erro se houver */}
              {errorMessage && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-medium">
                  {errorMessage}
                </div>
              )}

              {/* Botão de Envio */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-rose-500/30 transform active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={16} />
                    Enviar Relatório de Erro
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ErrorReportModal;
